import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import {
  IOrganization,
  IOrganizationGroup,
  IPage,
  IUser,
} from 'common/src/types/interfaces';

import { NEW_ID } from 'portal/src/constants/ReservedIDs';

import {
  deleteOrganizationGroup,
  getOrganizationGroups,
} from '../../api/OrganizationGroupsService';
import DataTable from '../common/DataTable';

const OrganizationGroups = () => {
  const navigate = useNavigate();

  const {
    data: organizationGroups,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['organizationGroups'],
    queryFn: async () => await getOrganizationGroups(),
    initialData: { data: [] } as IPage<IOrganizationGroup>,
  });

  const gridColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'organizations',
      headerName: 'Organizations',
      flex: 1,
      valueGetter: (value: Partial<IOrganization>[]) => {
        const organizations = value?.map((org) => org.name);
        return organizations?.join(', ');
      },
    },
    {
      field: 'users',
      headerName: 'Users',
      flex: 1,
      valueGetter: (value: Partial<IUser>[]) => {
        const users = value?.map((user) => user.name);
        return users?.join(', ');
      },
    },
  ];

  const handleNavigate = (id: string) => navigate(`./${id}`);

  return (
    <DataTable
      actionsConfig={{
        readActionConfig: {
          onClick: (id) => handleNavigate(id),
        },
        deleteConfig: {
          targetStr: 'organization group',
          onDeleteClick: async (id) => {
            deleteOrganizationGroup(id);
            await refetch();
          },
          snackbarMsgKey: 'ORGANIZATION_GROUP',
        },
      }}
      columns={gridColumns}
      contextName={NESTED_ROUTES.SYSTEM_ADMINISTRATION.ORGANIZATION_GROUPS}
      dataFetchError={isError}
      loading={isFetching}
      rows={organizationGroups.data}
      toolbarSlotProps={{
        onNewButtonClick: () => handleNavigate(NEW_ID),
        showFilters: true,
      }}
    />
  );
};

export default OrganizationGroups;
